import type {CSSProperties} from 'react';

export type StoreProductCardImageInput = {
    productId: number;
    name: string;
    alt?: string;
    customizationMethod: number;
    imageFull?: string | null;
    imageThumb?: string | null;
    imageItemId?: string | null;
    cdnDomain?: string | null;
    storePageWidth?: number | null;
    storePageHeight?: number | null;
    storePageWidthXs?: number | null;
    storePageHeightXs?: number | null;
};

export type StoreCardImageStage = 'preferred' | 'original' | 'thumbnail';

export type StoreCardImageStep = {
    stage: StoreCardImageStage;
    url: string;
};

export type StoreCardImageView = {type: 'preferred'; webpSrc: string; originalSrc: string} | {type: 'original'; src: string} | {type: 'thumbnail'; src: string} | {type: 'placeholder'};

export type StoreCardImageFailureInput = {
    stage: StoreCardImageStage;
    stageUrl: string;
    currentSrc: string;
    originalSrc: string;
};

export type StoreCardImagePresentation = {
    width?: number;
    height?: number;
    className: string;
    style: CSSProperties;
};

const GENERATED_IMAGE_SIZE = '308_308';

function isUsableCdnDomain(value: string | null | undefined): value is string {
    return typeof value === 'string' && value.trim() !== '';
}

// Missing ids are interpolated as the word "undefined" by a template string.
// Reject that token and blank names so the card never requests them.
function isUsableFileName(value: string | null | undefined): value is string {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return trimmed !== '' && trimmed.toLowerCase() !== 'undefined';
}

function positiveDimension(value: number | null | undefined): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function addUniqueStep(steps: StoreCardImageStep[], seen: Set<string>, stage: StoreCardImageStage, url: string): void {
    if (seen.has(url)) return;
    seen.add(url);
    steps.push({stage, url});
}

export function catalogThumbnailUrl(cdnDomain: string, imageThumb: string): string {
    // Path, query, and fragment of an absolute URL can be case-sensitive. Match before filename lowercasing.
    if (/^https?:\/\/.+/i.test(imageThumb)) return imageThumb;
    const fileName = imageThumb.toLowerCase();
    return `https://${cdnDomain}/images/products/thumb/${fileName}`;
}

function addFullImageSteps(steps: StoreCardImageStep[], seen: Set<string>, cdnDomain: string, imageFull: string): void {
    const fileName = imageFull.toLowerCase();
    const original = `https://${cdnDomain}/images/products/full/${fileName}`;
    const webp = `https://${cdnDomain}/images/products/full/${fileName.replace('.png', '.webp').replace('.jpg', '.webp')}`;
    if (webp !== original) addUniqueStep(steps, seen, 'preferred', webp);
    addUniqueStep(steps, seen, 'original', original);
}

function addGeneratedImageSteps(steps: StoreCardImageStep[], seen: Set<string>, cdnDomain: string, imageItemId: string, productId: number): void {
    const fileName = `${imageItemId.toLowerCase()}_${productId}_${GENERATED_IMAGE_SIZE}`;
    addUniqueStep(steps, seen, 'preferred', `https://${cdnDomain}/images/products/gen/products/${fileName}.webp`);
    addUniqueStep(steps, seen, 'original', `https://${cdnDomain}/images/products/gen/products/${fileName}.png`);
}

export function getStoreCardImageSteps(input: StoreProductCardImageInput): StoreCardImageStep[] {
    const steps: StoreCardImageStep[] = [];
    const seen = new Set<string>();
    if (!isUsableCdnDomain(input.cdnDomain)) return steps;

    if (input.customizationMethod < 2) {
        if (isUsableFileName(input.imageFull)) addFullImageSteps(steps, seen, input.cdnDomain, input.imageFull);
    } else if (isUsableFileName(input.imageItemId) && Number.isFinite(input.productId)) {
        addGeneratedImageSteps(steps, seen, input.cdnDomain, input.imageItemId, input.productId);
    }

    if (isUsableFileName(input.imageThumb)) {
        addUniqueStep(steps, seen, 'thumbnail', catalogThumbnailUrl(input.cdnDomain, input.imageThumb));
    }

    return steps;
}

export function getStoreCardImageSourceKey(input: StoreProductCardImageInput): string {
    return JSON.stringify([input.productId, input.customizationMethod, getStoreCardImageSteps(input).map(step => step.url)]);
}

export function getStoreCardImageView(input: StoreProductCardImageInput, failedUrls: readonly string[]): StoreCardImageView {
    const remaining = getStoreCardImageSteps(input).filter(candidate => !failedUrls.includes(candidate.url));
    // A WebP candidate is only selectable while its original file is still available.
    const step = remaining.find(candidate => candidate.stage !== 'preferred' || remaining.some(other => other.stage === 'original'));
    if (!step) return {type: 'placeholder'};

    if (step.stage === 'preferred') {
        const original = remaining.find(candidate => candidate.stage === 'original');
        if (original) return {type: 'preferred', webpSrc: step.url, originalSrc: original.url};
    }

    if (step.stage === 'original') return {type: 'original', src: step.url};
    if (step.stage === 'thumbnail') return {type: 'thumbnail', src: step.url};
    return {type: 'placeholder'};
}

export function recordStoreCardImageFailure(failedUrls: readonly string[], input: StoreCardImageFailureInput): readonly string[] {
    const next = failedUrls.slice();
    const add = (url: string) => {
        if (typeof url !== 'string' || url.trim() === '' || next.includes(url)) return;
        next.push(url);
    };

    add(input.stageUrl);

    // A failed WebP <source> can report the <img> address as currentSrc before that file is requested.
    // Leave the original candidate available until its own stage errors.
    if (input.stage === 'preferred' && input.currentSrc === input.originalSrc) {
        return next.length === failedUrls.length ? failedUrls : next;
    }

    add(input.currentSrc);
    return next.length === failedUrls.length ? failedUrls : next;
}

export function getStoreCardImagePresentation(input: StoreProductCardImageInput): StoreCardImagePresentation {
    const width = positiveDimension(input.storePageWidth);
    const height = positiveDimension(input.storePageHeight);

    if (input.customizationMethod >= 2) {
        const hasCustomSize = width !== undefined && height !== undefined;
        return {
            width: hasCustomSize ? width : 308,
            height: hasCustomSize ? height : 308,
            className: `store-img store-img-gen${hasCustomSize ? '' : ' store-img-gen-default'}`,
            style: {maxHeight: 250},
        };
    }

    const widthXs = positiveDimension(input.storePageWidthXs);
    const heightXs = positiveDimension(input.storePageHeightXs);
    return {
        width,
        height,
        className: `store-img${widthXs !== undefined || heightXs !== undefined ? ' store-img-xs' : ''}`,
        style: {
            maxHeight: 250,
            ...(widthXs !== undefined ? {['--store-page-width-xs' as string]: `${widthXs}px`} : {}),
            ...(heightXs !== undefined ? {['--store-page-height-xs' as string]: `${heightXs}px`} : {}),
        },
    };
}

export function storeCardPlaceholderSrc(width?: number, height?: number): string {
    const placeholderWidth = width && width > 0 ? width : 308;
    const placeholderHeight = height && height > 0 ? height : 308;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${placeholderWidth}" height="${placeholderHeight}" viewBox="0 0 ${placeholderWidth} ${placeholderHeight}"><rect width="100%" height="100%" fill="#f3f3f3"/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
